import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { Badge, DataTable, ErrorState, formatBytes, formatNumber, formatRelative, Notice, PageHeader, Panel, Stat, StatGroup, StatusBadge } from "@hostdock/ui";
import { api } from "../api";
import { actionLabel, ActorCell } from "../lib/audit-labels";
import { useSession } from "../session";

export function DashboardPage() {
  const { session } = useSession();
  const q = useQuery({ queryKey: ["dashboard"], queryFn: api.dashboard, refetchInterval: 30_000 });
  const d = q.data;
  const loading = q.isPending;

  if (q.error) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;

  return (
    <>
      <PageHeader title={`Welcome, ${session.user.name.split(" ")[0]}`} description="Live overview of the HostDock control plane." />
      <div className="flex flex-col gap-5">
        {d?.admins && d.admins.withoutMfa > 0 && (
          <Notice tone="warning" title={`${d.admins.withoutMfa} active admin${d.admins.withoutMfa === 1 ? " has" : "s have"} not enabled two-factor authentication`}>
            Require two-factor authentication for admins in <Link to="/settings" className="text-accent hover:underline">Settings</Link>.
          </Notice>
        )}

        {(loading || d?.infrastructure) && (
          <>
            <StatGroup className="grid-cols-2 lg:grid-cols-4">
              <Stat label="Websites" loading={loading} value={formatNumber(d?.infrastructure?.websites)} />
              <Stat label="Databases" loading={loading} value={formatNumber(d?.infrastructure?.databases)} />
              <Stat label="Active containers" loading={loading} value={formatNumber(d?.infrastructure?.containers)} />
              <Stat
                label="Storage used"
                loading={loading}
                value={d?.infrastructure?.storageTotal ? `${((d.infrastructure.storageUsed / d.infrastructure.storageTotal) * 100).toFixed(0)}%` : "—"}
                hint={d?.infrastructure?.storageTotal ? `${formatBytes(d.infrastructure.storageUsed)} of ${formatBytes(d.infrastructure.storageTotal)}` : undefined}
              />
            </StatGroup>
            <StatGroup className="grid-cols-2 lg:grid-cols-4">
              <Stat label="Servers" loading={loading} value={formatNumber(d?.infrastructure?.servers)} hint={d?.infrastructure?.provisioning ? `${d.infrastructure.provisioning} provisioning` : undefined} />
              <Stat label="Healthy" loading={loading} value={formatNumber(d?.infrastructure?.healthy)} tone="success" />
              <Stat label="Warning" loading={loading} value={formatNumber(d?.infrastructure?.warning)} tone={d?.infrastructure?.warning ? "warning" : "default"} />
              <Stat label="Offline" loading={loading} value={formatNumber(d?.infrastructure?.offline)} tone={d?.infrastructure?.offline ? "danger" : "default"} hint={d?.infrastructure?.failed ? `${d.infrastructure.failed} failed provisioning` : undefined} />
            </StatGroup>
            {d?.infrastructure && d.infrastructure.regions.length > 0 && (
              <div className="grid gap-4 md:grid-cols-2">
                {d.infrastructure.regions.map((r) => (
                  <Panel key={r.id} title={<span className="flex items-center gap-2">{r.name} <Badge>{r.code}</Badge></span>} actions={<Link to={`/infrastructure/servers?regionId=${r.id}`} className="text-[12.5px] text-accent hover:underline">Servers</Link>}>
                    <dl className="grid grid-cols-3 gap-y-2 text-[13px] sm:grid-cols-6">
                      {[
                        ["Nodes", r.stats.servers], ["Healthy", r.stats.healthy], ["Active", r.stats.active],
                        ["CPU", r.stats.cpuPercent === null ? "—" : `${r.stats.cpuPercent.toFixed(0)}%`],
                        ["RAM", r.stats.memPercent === null ? "—" : `${r.stats.memPercent.toFixed(0)}%`],
                        ["Storage", r.stats.diskPercent === null ? "—" : `${r.stats.diskPercent.toFixed(0)}%`],
                      ].map(([k, v]) => (
                        <div key={String(k)}>
                          <dt className="text-[11.5px] tracking-wide text-muted uppercase">{k}</dt>
                          <dd className="text-[17px] font-semibold tabular-nums">{v}</dd>
                        </div>
                      ))}
                    </dl>
                  </Panel>
                ))}
              </div>
            )}
          </>
        )}

        {(loading || d?.customers) && (
          <StatGroup className="grid-cols-1 sm:grid-cols-3">
            <Stat label="Customers" loading={loading} value={formatNumber(d?.customers?.total)} />
            <Stat label="Active" loading={loading} value={formatNumber(d?.customers?.active)} tone="success" />
            <Stat label="Suspended" loading={loading} value={formatNumber(d?.customers?.suspended)} tone={d?.customers?.suspended ? "warning" : "default"} />
          </StatGroup>
        )}

        {(loading || d?.admins || d?.sessions || d?.security) && (
          <StatGroup className="grid-cols-2 lg:grid-cols-5">
            <Stat label="Admins" loading={loading} value={formatNumber(d?.admins?.active)} hint={d?.admins ? `${d.admins.total} total` : undefined} />
            <Stat label="Admin sessions" loading={loading} value={formatNumber(d?.sessions?.activeAdmin)} />
            <Stat label="Customer sessions" loading={loading} value={formatNumber(d?.sessions?.activeCustomer)} />
            <Stat label="Failed sign-ins (24h)" loading={loading} value={formatNumber(d?.security?.failedLoginsLast24h)} tone={d?.security?.failedLoginsLast24h ? "warning" : "default"} />
            <Stat label="Locked accounts" loading={loading} value={formatNumber(d?.security?.lockedAccounts)} tone={d?.security?.lockedAccounts ? "danger" : "default"} />
          </StatGroup>
        )}

        {(loading || d?.jobs) && (
          <StatGroup className="grid-cols-1 sm:grid-cols-3">
            <Stat label="Jobs queued" loading={loading} value={formatNumber(d?.jobs?.queued)} />
            <Stat label="Jobs running" loading={loading} value={formatNumber(d?.jobs?.running)} />
            <Stat label="Jobs failed (24h)" loading={loading} value={formatNumber(d?.jobs?.failedLast24h)} tone={d?.jobs?.failedLast24h ? "danger" : "default"} />
          </StatGroup>
        )}

        {(loading || d?.recentAudit) && (
          <Panel title="Recent activity" actions={<Link to="/security/audit" className="text-[12.5px] text-accent hover:underline">View audit log</Link>} flush>
            <DataTable
              rows={d?.recentAudit ?? undefined}
              loading={loading}
              rowKey={(r) => r.id}
              skeletonRows={5}
              columns={[
                { key: "when", header: "When", cell: (r) => <span className="whitespace-nowrap text-muted">{formatRelative(r.occurredAt)}</span> },
                { key: "action", header: "Action", cell: (r) => actionLabel(r.action) },
                { key: "actor", header: "Actor", cell: (r) => <ActorCell entry={r} />, hideOnMobile: true },
                { key: "result", header: "Result", cell: (r) => <StatusBadge status={r.result} /> },
              ]}
            />
          </Panel>
        )}

        {d && !d.infrastructure && !d.customers && !d.admins && !d.jobs && !d.recentAudit && (
          <Notice title="Your role has no dashboard sections">Ask an administrator to grant view permissions for the areas you work on.</Notice>
        )}
      </div>
    </>
  );
}
